/**
 * Function to round only values > 0
 * @param {*} value
 * @returns
 */
export const customRound = (value) => {
  const num = Number(value);
  if (isNaN(num) || num < 0) {
    return 0;
  }
  if (num < 1) {
    return Number(num.toFixed(1));
  }
  return Math.round(num);
};

/**
 * Request the custom and generic foods for the search results
 * @param {*} searchValue
 * @param {*} api
 * @returns
 */
export const getFilteredFoods = async (searchValue, sourceTags, api) => {
  const res = await api("search-generic-foods", {
    query: { searchValue, sourceTags },
  });
  const resJSON = await res.json();
  const data = resJSON?.data || [];
  return data;
};

/**
 * Calculate the nutrients values of foods, meals and meal planners
 * @param {*} foods
 * @param {*} foodKey
 * @returns
 */
export const calculateMacros = ({ isMeal, isRecipe, foods, itemDetails }) => {
  const {
    itemProteins = 0,
    itemCarbs = 0,
    itemFats = 0,
    itemCalories = 0,
    foodKey = "foodDetails",
  } = itemDetails;

  if (isMeal) {
    const totals = foods.reduce(
      (totals, food) => {
        const {
          quantityNumber,
          unitOfMeasure,
          type,
          [foodKey]: foodItem,
        } = food;

        if (!foodItem) return totals;

        if (type === "recipe") {
          const recipeFactor = quantityNumber / (foodItem.quantityNumber || 1);

          return {
            proteins: totals.proteins + foodItem.proteins * recipeFactor,
            carbs: totals.carbs + foodItem.carbs * recipeFactor,
            fats: totals.fats + foodItem.fats * recipeFactor,
            calories: totals.calories + foodItem.calories * recipeFactor,
          };
        }

        const {
          proteins = 0,
          carbs = 0,
          fats = 0,
          calories = 0,
          measurementsValue = 1,
        } = foodItem;

        let factor = 1;

        if (unitOfMeasure === "g" || unitOfMeasure === "ml") {
          factor = quantityNumber / measurementsValue;
        } else if (unitOfMeasure in conversionRates) {
          const { convertedValue } = convertMeasurement(
            quantityNumber,
            unitOfMeasure
          );
          factor = convertedValue / measurementsValue;
        } else {
          const unitValue = calculateMeasurementPerUnit(
            foodItem.quantityNumber,
            measurementsValue
          );
          factor = (quantityNumber * unitValue) / measurementsValue;
        }

        return {
          proteins: totals.proteins + proteins * factor,
          carbs: totals.carbs + carbs * factor,
          fats: totals.fats + fats * factor,
          calories: totals.calories + calories * factor,
        };
      },
      { proteins: 0, carbs: 0, fats: 0, calories: 0 }
    );

    return {
      proteins: customRound(totals.proteins),
      carbs: customRound(totals.carbs),
      fats: customRound(totals.fats),
      calories: customRound(totals.calories),
    };
  }

  if (isRecipe) {
    return {
      proteins: itemProteins,
      carbs: itemCarbs,
      fats: itemFats,
      calories: customRound(itemProteins * 4 + itemCarbs * 4 + itemFats * 9),
    };
  }

  return {
    proteins: itemProteins,
    carbs: itemCarbs,
    fats: itemFats,
    calories: itemCalories,
  };
};

/**
 * Function to recalculate the nutrients values with a new serving size
 * @param {*} param0
 * @returns
 */
export const reCalculateMacros = ({ itemDetails }) => {
  const {
    itemProteins = 0,
    itemCarbs = 0,
    itemFats = 0,
    itemCalories = 0,
    quantityNumber = 1,
    customQuantityNumber = 1,
    measurementsValue = 1,
    customUnitOfMeasure = "custom",
    type,
    skipRecalculation = false,
  } = itemDetails;

  if (skipRecalculation) {
    return {
      proteins: itemProteins,
      carbs: itemCarbs,
      fats: itemFats,
      calories: itemCalories,
    };
  }

  let factor;

  if (type === "recipe") {
    //if it's recipe we are using the direct relation
    factor = customQuantityNumber / quantityNumber;
  } else if (customUnitOfMeasure === "g" || customUnitOfMeasure === "ml") {
    //if the user selects g or ml we are using measurementsValue
    factor = customQuantityNumber / measurementsValue;
  } else if (customUnitOfMeasure in conversionRates) {
    //of the user selects another unit, we are converting first to g or ml
    const { convertedValue } = convertMeasurement(
      customQuantityNumber,
      customUnitOfMeasure
    );
    factor = convertedValue / measurementsValue;
  } else {
    //custom case
    const unitValue = calculateMeasurementPerUnit(
      quantityNumber,
      measurementsValue
    );
    factor = (customQuantityNumber * unitValue) / measurementsValue;
  }

  const updatedValues = {
    proteins: customRound(itemProteins * factor),
    carbs: customRound(itemCarbs * factor),
    fats: customRound(itemFats * factor),
    calories: customRound(itemCalories * factor),
  };
  return updatedValues;
};

/**
 * Parse the foods that are inside of a meal (get)
 * @param {*} foods
 * @returns
 */
export const transformFoodsToGetTheMeal = (foods = []) => {
  return foods.map(({ quantityNumber, type, foodDetails, unitOfMeasure }) => ({
    customQuantityNumber: quantityNumber,
    customUnitOfMeasure: unitOfMeasure,
    type,
    ...foodDetails,
  }));
};

/**
 * Combines the transformation of foods and recalculates their macros (for the meals in the food library)
 * @param {Array} foods - Array of food items in a meal
 * @returns {Array} - Transformed food items with recalculated macros
 */
export const transformMealFoods = (foods = []) => {
  return foods.map(({ quantityNumber, type, foodDetails, unitOfMeasure }) => {
    const transformed = {
      customQuantityNumber: quantityNumber,
      customUnitOfMeasure: unitOfMeasure,
      type,
      ...foodDetails,
    };

    const { proteins, carbs, fats, calories, ...rest } = transformed;

    const itemDetails = {
      itemProteins: proteins,
      itemCarbs: carbs,
      itemFats: fats,
      itemCalories: calories,
      ...rest,
      foodKey: "foodId",
      skipRecalculation: false,
    };

    const recalculatedMacros = reCalculateMacros({ itemDetails });

    return {
      ...transformed,
      ...recalculatedMacros,
    };
  });
};

/**
 * Function to round the values
 * @param {*} value
 * @returns
 */
export const formatNutritionValue = (value) =>
  value % 1 === 0 ? value : value.toFixed(1);

export const weightUnits = ["g", "oz", "lb"];
export const volumeUnits = ["ml", "tsp", "tbsp", "fl oz", "cup"];

export const completeWeightUnits = [
  { value: "g", text: "g" },
  { value: "oz", text: "oz - 28.35g" },
  { value: "lb", text: "lb - 453.59g" },
];

export const completeVolumeUnits = [
  { value: "ml", text: "ml" },
  { value: "tsp", text: "tsp - 4.93ml" },
  { value: "tbsp", text: "tbsp - 14.79ml" },
  { value: "fl oz", text: "fl oz - 29.57ml" },
  { value: "cup", text: "cup - 236.59ml" },
];

/**
 * Get the measurement for custom cases
 * @param {*} quantityNumber
 * @param {*} measurementsValue
 * @returns
 */
export const calculateMeasurementPerUnit = (
  quantityNumber,
  measurementsValue
) => {
  if (quantityNumber === 0) return null;
  const result = measurementsValue / quantityNumber;
  return Number.isInteger(result) ? result : parseFloat(result.toFixed(2));
};

export const conversionRates = {
  oz: 28.35, // 1 oz = 28.35 g
  lb: 453.59, // 1 lb = 453.59 g
  tsp: 4.93, // 1 tsp = 4.93 ml
  tbsp: 14.79, // 1 tbsp = 14.79 ml
  "fl oz": 29.57, // 1 fl oz = 29.57 ml
  cup: 236.59, // 1 cup = 236.59 ml
};

/**
 * Function to handle the unit conversions
 * @param {*} value
 * @param {*} unit
 * @returns
 */
export const convertMeasurement = (value, unit) => {
  if (weightUnits.includes(unit)) {
    return {
      convertedValue: value * (conversionRates[unit] || 1),
      convertedUnit: "g",
    };
  } else if (volumeUnits.includes(unit)) {
    return {
      convertedValue: value * (conversionRates[unit] || 1),
      convertedUnit: "ml",
    };
  }
  return { convertedValue: value, convertedUnit: unit };
};

/**
 * Parse the food info to edit a food day
 * @param {*} foods
 * @returns
 */
export const parseFoodData = (foods) => {
  return foods
    .filter((item) => item.foodId && Object.keys(item.foodId).length > 0)
    .map(({ quantityNumber, unitOfMeasure, type, foodId }) => ({
      ...foodId,
      customQuantityNumber: quantityNumber,
      customUnitOfMeasure: unitOfMeasure,
      type: type,
      skipRecalculation: true,
    }));
};

/**
 * Function to show the unit of the food
 * @param {*} quantityNumber
 * @param {*} type
 * @param {*} customUnitOfMeasure
 * @param {*} quantityDescription
 * @returns
 */

export const parseUnitToShow = (
  quantityNumber,
  type,
  customUnitOfMeasure,
  quantityDescription
) => {
  const unitToShow =
    type === "recipe" ||
    (quantityDescription === "Serving" && customUnitOfMeasure === "custom")
      ? quantityNumber > 1
        ? `${quantityDescription.toLowerCase()}s`
        : quantityDescription.toLowerCase()
      : customUnitOfMeasure === "custom"
      ? quantityDescription
      : customUnitOfMeasure;

  return unitToShow;
};

/**
 * Parse the food information inside the meals
 * @param {*} foods
 * @returns
 */
export const transformFoodsForMeals = (foods) =>
  foods.map((food) => ({
    type: food.type,
    foodId: food._id,
    quantityNumber: Number(food.customQuantityNumber),
    unitOfMeasure: food.customUnitOfMeasure,
  }));

/**
 * Function to know if a date is valid
 * @param {*} date
 * @returns
 */
export const isValidDate = (date) =>
  /^\d{8}$/.test(date) ||
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(date);

/**
 * Function to get the foods that already exists in a day
 * @param {*} selectedWeek
 * @param {*} mealPlannerDate
 * @param {*} mealPlannerMeal
 * @returns
 */
export const getFilteredFoodsForAddModal = (
  selectedWeek,
  mealPlannerDate,
  mealPlannerMeal
) => {
  const isDateValid = isValidDate(mealPlannerDate);

  const selectedDay = isDateValid
    ? selectedWeek.find(({ date }) => date === mealPlannerDate)
    : (() => {
        const match = mealPlannerDate.match(/Day (\d+)/);
        if (!match) return null;
        const dayIndex = Number(match[1]) - 1;
        return selectedWeek[dayIndex] || null;
      })();

  if (!mealPlannerMeal) return [];

  return parseFoodData(
    selectedDay?.meals.find(
      ({ type }) => type.toString() === mealPlannerMeal.toString()
    )?.foods || []
  );
};
